import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import { CookieBanner, useCookieConsent } from './components/CookieBanner';
import { ChatAssistant } from './components/ChatAssistant';
import SEO from './components/SEO';

// Contexts
import { ThemeProvider, useRouteTheme } from './contexts/ThemeContext';
import { LocationProvider, useLocation as useLocationCtx } from './contexts/LocationContext';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerProvider } from './contexts/CustomerContext';
import { SiteSettingsProvider, useSiteSettings } from './contexts/SiteSettingsContext';

// Layout Components (always loaded)
import Navbar from './components/layout/Navbar';
import ShopNavbar from './components/layout/ShopNavbar';
import Footer from './components/layout/Footer';
import LocationPicker from './components/layout/LocationPicker';
import ServiceStatusBanner from './components/ServiceStatusBanner';

// Critical path – eager load Home
import Home from './pages/Home';

// Lazy-loaded Pages
const Franchise = React.lazy(() => import('./pages/Franchise'));
const Menu = React.lazy(() => import('./pages/Menu'));
const StaffOrder = React.lazy(() => import('./pages/StaffOrder'));
const CleanStation = React.lazy(() => import('./pages/CleanStation'));
const PrepStation = React.lazy(() => import('./pages/PrepStation'));
const KitchenDisplay = React.lazy(() => import('./pages/KitchenDisplay'));
const AdminPrep = React.lazy(() => import('./pages/admin/AdminPrep'));
const AdminCleaning = React.lazy(() => import('./pages/admin/AdminCleaning'));
const AdminContracts = React.lazy(() => import('./pages/admin/AdminContracts'));
const AdminHR = React.lazy(() => import('./pages/admin/AdminHR'));
const MenuBoard = React.lazy(() => import('./pages/MenuBoard'));
const BoardHtml = React.lazy(() => import('./pages/BoardHtml'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const OrderConfirmation = React.lazy(() => import('./pages/OrderConfirmation'));
const MyOrders = React.lazy(() => import('./pages/MyOrders'));

// Admin – Lazy
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminSetPassword = React.lazy(() => import('./pages/admin/SetPassword'));
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrders'));
const AdminPOS = React.lazy(() => import('./pages/admin/AdminPOS'));
const AdminPOSCashiers = React.lazy(() => import('./pages/admin/AdminPOSCashiers'));
const AdminPOSLive = React.lazy(() => import('./pages/admin/AdminPOSLive'));
const AdminCustomers = React.lazy(() => import('./pages/admin/AdminCustomers'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminFranchise = React.lazy(() => import('./pages/admin/AdminFranchise'));
const AdminFranchiseInvoices = React.lazy(() => import('./pages/admin/AdminFranchiseInvoices'));
const AdminFocalPoint = React.lazy(() => import('./pages/admin/AdminFocalPoint'));
const AdminMenuEditor = React.lazy(() => import('./pages/admin/AdminMenuEditor'));
const AdminMenuAvailability = React.lazy(() => import('./pages/admin/AdminMenuAvailability'));
const AdminDailyDeals = React.lazy(() => import('./pages/admin/AdminDailyDeals'));
const AdminDeliveryZones = React.lazy(() => import('./pages/admin/AdminDeliveryZones'));
const AdminDiscounts = React.lazy(() => import('./pages/admin/AdminDiscounts'));
const AdminKiosk = React.lazy(() => import('./pages/admin/AdminKiosk'));
const AdminBackups = React.lazy(() => import('./pages/admin/AdminBackups'));
const AdminMarginReports = React.lazy(() => import('./pages/admin/AdminMarginReports'));
const AdminVouchers = React.lazy(() => import('./pages/admin/AdminVouchers'));
const AdminVehicles = React.lazy(() => import('./pages/admin/AdminVehicles'));
const AdminPayroll = React.lazy(() => import('./pages/admin/AdminPayroll'));
const AdminComplaints = React.lazy(() => import('./pages/admin/AdminComplaints'));
const AdminEocloud = React.lazy(() => import('./pages/admin/AdminEocloud'));
const ShortRedirect = React.lazy(() => import('./pages/ShortRedirect'));
const OrderComplaint = React.lazy(() => import('./pages/OrderComplaint'));
const AdminInventory = React.lazy(() => import('./pages/admin/AdminInventory'));
const DriverApp = React.lazy(() => import('./pages/DriverApp'));
const AdminDisplays = React.lazy(() => import('./pages/admin/AdminDisplays'));
const SchaufensterDisplay = React.lazy(() => import('./pages/SchaufensterDisplay'));
const Videothek = React.lazy(() => import('./pages/Videothek'));
const AdminSocialMedia = React.lazy(() => import('./pages/admin/AdminSocialMedia'));
const AdminSocialStoriesApproval = React.lazy(() => import('./pages/admin/AdminSocialStoriesApproval'));
const AdminSocialPlanner = React.lazy(() => import('./pages/admin/AdminSocialPlanner'));
const AdminReviewStories = React.lazy(() => import('./pages/admin/AdminReviewStories'));
const AdminWeeklyPlan = React.lazy(() => import('./pages/admin/AdminWeeklyPlan'));
const AdminPayments = React.lazy(() => import('./pages/admin/AdminPayments'));
const AdminMarketing = React.lazy(() => import('./pages/admin/AdminMarketing'));
const AdminAutoCampaigns = React.lazy(() => import('./pages/admin/AdminAutoCampaigns'));
const AdminLieferandoStudio = React.lazy(() => import('./pages/admin/AdminLieferandoStudio'));

// Display – Lazy
const Display = React.lazy(() => import('./pages/Display'));
const StaffPOS = React.lazy(() => import('./pages/staff/StaffPOS'));

// Kiosk – Lazy
const KioskPage = React.lazy(() => import('./pages/kiosk/KioskPage'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const NewsletterBestaetigen = React.lazy(() => import('./pages/NewsletterBestaetigen'));
const Allergene = React.lazy(() => import('./pages/Allergene'));
const FreundeWerben = React.lazy(() => import('./pages/FreundeWerben'));

function RouteLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-3 border-[#A81C1C] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const locationsCtx = useLocationCtx();
  useRouteTheme(location.pathname);

  // Persist `?code=XYZ` from any incoming URL into sessionStorage so the
  // Checkout page can auto-fill the discount code, even after the customer
  // navigates through the menu first. Cleared on order success.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = (params.get('code') || '').trim().toUpperCase();
    if (code && /^[A-Z0-9-]{3,30}$/.test(code)) {
      sessionStorage.setItem('zozo_pending_discount_code', code);
    }
  }, [location.search]);

  const isAdmin = location.pathname.startsWith('/admin');
  const isShop = location.pathname.startsWith('/bestellen') || location.pathname.startsWith('/meine-bestellungen') || location.pathname.startsWith('/reklamation') || location.pathname.startsWith('/s/');
  const isKiosk = location.pathname.startsWith('/kiosk');
  const isDisplay = location.pathname.startsWith('/display') || location.pathname.startsWith('/videothek');
  const isStaffLager = location.pathname.startsWith('/lager/');
  const isCleanStation = location.pathname.startsWith('/clean/') || location.pathname.startsWith('/prep/') || location.pathname.startsWith('/kds/') || location.pathname.startsWith('/board/');
  const isStaff = location.pathname.startsWith('/staff') || location.pathname.startsWith('/fahrer') || location.pathname.startsWith('/mitarbeiter');

  // Team-Links: eigener App-Name + Icon beim "Zum Home-Bildschirm hinzufügen"
  useEffect(() => {
    const p = location.pathname;
    const cfg = p.startsWith('/lager/') ? { title: 'ZOZO Lager', icon: '/icons/icon-lager.png', theme: '#A81C1C' }
      : p.startsWith('/clean/') ? { title: 'ZOZO Putzplan', icon: '/icons/icon-reinigung.png', theme: '#0E7C6B' }
      : p.startsWith('/kds/') ? { title: 'ZOZO Küche', icon: '/icons/icon-kds.png', theme: '#26211E' }
      : null;
    if (!cfg) return;
    document.title = cfg.title;
    let apple = document.querySelector("link[rel='apple-touch-icon']");
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      document.head.appendChild(apple);
    }
    apple.href = cfg.icon;
    const manifest = {
      name: cfg.title,
      short_name: cfg.title.replace('ZOZO ', ''),
      start_url: p,
      scope: p,
      display: 'standalone',
      background_color: '#1a1614',
      theme_color: cfg.theme,
      icons: [{ src: window.location.origin + cfg.icon, sizes: '512x512', type: 'image/png' }],
    };
    let m = document.querySelector("link[rel='manifest']");
    if (!m) {
      m = document.createElement('link');
      m.rel = 'manifest';
      document.head.appendChild(m);
    }
    m.href = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/json' }));
  }, [location.pathname]);

  // Reinigungs-Tablet-Link (ohne Login) — fullscreen, kein Shop-Chrome
  if (isCleanStation) {
    return (
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/clean/:token" element={<CleanStation />} />
          <Route path="/board/:screen" element={<BoardHtml />} />
              <Route path="/board-old/:screen" element={<MenuBoard />} />
          <Route path="/prep/:token" element={<PrepStation />} />
          <Route path="/kds/:token" element={<KitchenDisplay />} />
        </Routes>
      </Suspense>
    );
  }

  // Mitarbeiter-Bestell-Link (ohne Login) — fullscreen, kein Shop-Chrome
  if (isStaffLager) {
    return (
      <>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/lager/:token" element={<StaffOrder />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // Staff terminals (POS / Kitchen Display) — fullscreen, no shop chrome.
  if (isStaff) {
    const isDriverOnTheRoad =
      location.pathname.startsWith('/fahrer') || location.pathname.startsWith('/mitarbeiter') || location.pathname.startsWith('/staff/fahrer');
    const driverBranches = locationsCtx?.locations || [];
    return (
      <>
        {isDriverOnTheRoad && <ServiceStatusBanner branches={driverBranches} />}
        <Suspense fallback={<div style={{ background: '#0C0A09', width: '100vw', height: '100vh' }} />}>
          <Routes>
            <Route path="/staff/pos" element={<StaffPOS />} />
            <Route path="/staff/fahrer" element={<DriverApp />} />
            <Route path="/fahrer" element={<DriverApp />} />
            <Route path="/mitarbeiter" element={<DriverApp />} />
            <Route path="/staff" element={<Navigate to="/staff/pos" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // Kiosk mode - fullscreen, no navbar/footer
  if (isKiosk) {
    return (
      <>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/kiosk/:locationSlug" element={<KioskPage />} />
            <Route path="/kiosk" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" />
      </>
    );
  }

  // Display mode - fullscreen, no UI chrome
  if (isDisplay) {
    return (
      <Suspense fallback={<div style={{ background: '#0C0A09', width: '100vw', height: '100vh' }} />}>
        <Routes>
          <Route path="/display/schaufenster" element={<SchaufensterDisplay />} />
          <Route path="/videothek" element={<Videothek />} />
          <Route path="/display/:screenId" element={<Display />} />
        </Routes>
      </Suspense>
    );
  }

  if (isAdmin) {
    return (
      <>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/passwort-setzen" element={<AdminSetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="pos" element={<AdminPOS />} />
              <Route path="pos/cashiers" element={<AdminPOSCashiers />} />
              <Route path="pos/live" element={<AdminPOSLive />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="franchise" element={<AdminFranchise />} />
              <Route path="franchise-rechnungen" element={<AdminFranchiseInvoices />} />
              <Route path="focal-point" element={<AdminFocalPoint />} />
              <Route path="menu" element={<AdminMenuEditor />} />
              <Route path="menu-verfuegbarkeit" element={<AdminMenuAvailability />} />
              <Route path="daily-deals" element={<AdminDailyDeals />} />
              <Route path="delivery-zones" element={<AdminDeliveryZones />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="kiosk" element={<AdminKiosk />} />
              <Route path="displays" element={<AdminDisplays />} />
              <Route path="social-media" element={<AdminSocialMedia />} />
              <Route path="social-media/approval" element={<Navigate to="/admin/social-media/stories" replace />} />
              <Route path="social-media/stories" element={<AdminSocialStoriesApproval />} />
              <Route path="social-media/planner" element={<AdminSocialPlanner />} />
              <Route path="review-stories" element={<AdminReviewStories />} />
              <Route path="social-media/weekly-plan" element={<AdminWeeklyPlan />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="marketing" element={<AdminMarketing />} />
              <Route path="auto-campaigns" element={<AdminAutoCampaigns />} />
              <Route path="lieferando-studio" element={<AdminLieferandoStudio />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="backups" element={<AdminBackups />} />
              <Route path="margin-reports" element={<AdminMarginReports />} />
              <Route path="vouchers" element={<AdminVouchers />} />
              <Route path="vehicles" element={<AdminVehicles />} />
              <Route path="lohn" element={<AdminPayroll />} />
              <Route path="reklamationen" element={<AdminComplaints />} />
              <Route path="kasse" element={<AdminEocloud />} />
              <Route path="lager" element={<AdminInventory />} />
              <Route path="reinigung" element={<AdminCleaning />} />
              <Route path="vertraege" element={<AdminContracts />} />
              <Route path="personalakten" element={<AdminHR />} />
              <Route path="vorbereitung" element={<AdminPrep />} />
            </Route>
          </Routes>
        </Suspense>
        <Toaster position="top-center" />
      </>
    );
  }

  if (isShop) {
    const branchesForBanner = locationsCtx?.locations || [];
    return (
      <div
        className="flex flex-col min-h-screen"
        style={{
          '--background': '40 6% 96%',
          '--foreground': '12 7% 15%',
          '--card': '0 0% 100%',
          '--card-foreground': '12 7% 15%',
          '--popover': '0 0% 100%',
          '--popover-foreground': '12 7% 15%',
          '--muted': '20 6% 90%',
          '--muted-foreground': '25 5% 45%',
          '--border': '20 6% 90%',
          '--input': '20 6% 90%',
          backgroundColor: '#FAFAF8',
          color: '#292524',
        }}
        data-theme="light"
      >
        <ServiceStatusBanner branches={branchesForBanner} />
        <ShopNavbar />
        <LocationPicker />
        <main className="flex-1 pt-[60px]">
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route path="/meine-bestellungen" element={<MyOrders />} />
              <Route path="/bestellen" element={<ShopRedirect />} />
              <Route path="/bestellen/:locationSlug" element={<Menu />} />
              <Route path="/bestellen/checkout" element={<Checkout />} />
              <Route path="/bestellen/bestaetigung/:orderNumber" element={<OrderConfirmation />} />
              <Route path="/reklamation/:orderNumber" element={<OrderComplaint />} />
              <Route path="/s/:code" element={<ShortRedirect />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <CookieBanner />
        <ChatAssistant />
        <Toaster position="top-center" />
      </div>
    );
  }

  // Landing pages (/, /franchise, /impressum, etc.) — always dark
  const branchesForLanding = locationsCtx?.locations || [];
  return (
    <div className="flex flex-col min-h-screen">
      <ServiceStatusBanner branches={branchesForLanding} />
      <Navbar />
      <LocationPicker />
      <main className="flex-1">
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/franchise" element={<Franchise />} />
            <Route path="/impressum" element={<StaticPage title="Impressum" />} />
            <Route path="/datenschutz" element={<StaticPage title="Datenschutz" />} />
            <Route path="/agb" element={<StaticPage title="AGB" />} />
            <Route path="/kontakt" element={<StaticPage title="Kontakt" />} />
            <Route path="/allergene" element={<Allergene />} />
            <Route path="/freunde-werben" element={<FreundeWerben />} />
            {/* Legacy redirects for SEO & old URLs */}
            <Route path="/menu" element={<Navigate to="/bestellen" replace />} />
            <Route path="/speisekarte" element={<Navigate to="/bestellen" replace />} />
            <Route path="/order" element={<Navigate to="/bestellen" replace />} />
            <Route path="/bestellung" element={<Navigate to="/bestellen" replace />} />
            <Route path="/warenkorb" element={<Navigate to="/bestellen" replace />} />
            <Route path="/cart" element={<Navigate to="/bestellen" replace />} />
            <Route path="/contact" element={<Navigate to="/kontakt" replace />} />
            <Route path="/about" element={<Navigate to="/" replace />} />
            <Route path="/ueber-uns" element={<Navigate to="/" replace />} />
            <Route path="/order/:orderNumber" element={<LegacyOrderRedirect />} />
            <Route path="/order-confirmation/:orderNumber" element={<LegacyOrderRedirect />} />
            <Route path="/newsletter/bestaetigen" element={<NewsletterBestaetigen />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster position="top-center" />
    </div>
  );
}

function ShopRedirect() {
  const savedSlug = localStorage.getItem('selectedLocation') || 'rellingen';
  return <Navigate to={`/bestellen/${savedSlug}${window.location.search}`} replace />;
}

function LegacyOrderRedirect() {
  const location = useLocation();
  const orderNumber = location.pathname.split('/').pop();
  return <Navigate to={`/bestellen/bestaetigung/${orderNumber}`} replace />;
}

function StaticPage({ title }) {
  const settings = useSiteSettings();
  const { locations } = useLocationCtx();
  const PATH_MAP = { 'Impressum': '/impressum', 'Datenschutz': '/datenschutz', 'AGB': '/agb', 'Kontakt': '/kontakt' };
  const DESC_MAP = {
    'Impressum': 'Impressum & Anbieterkennzeichnung von ZOZO Burger — Inhaber, Anschrift, Kontaktdaten, Umsatzsteuer-ID.',
    'Datenschutz': 'Datenschutzerklärung von ZOZO Burger nach DSGVO — Verarbeitung, Speicherung & Ihre Rechte.',
    'AGB': 'Allgemeine Geschäftsbedingungen (AGB) von ZOZO Burger für Online-Bestellungen, Lieferung und Abholung.',
    'Kontakt': 'Kontakt zu ZOZO Burger Rellingen & Henstedt-Ulzburg — Telefon, E-Mail, Adressen und Öffnungszeiten.',
  };

  // Helper: format opening hours from DB
  const formatHours = (loc) => {
    const hours = loc?.opening_hours;
    if (!hours) return 'Keine Angabe';
    const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    const entries = days.map(d => hours[d]).filter(Boolean);
    if (entries.length === 0) return 'Keine Angabe';
    // Check if all days are the same
    const first = entries[0];
    const allSame = entries.every(e => e.open === first.open && e.close === first.close);
    if (allSame && entries.length === 7) return `Mo-So ${first.open}-${first.close}`;
    return `Mo-So ${first.open}-${first.close}`;
  };

  const companyBlock = (
    <>
      {settings.shop_name}<br />
      {settings.company_owner}<br />
      {settings.company_street}<br />
      {settings.company_postal_code} {settings.company_city}
    </>
  );

  const content = {
    'Impressum': (
      <>
        <h2>Angaben gemäß &sect; 5 TMG</h2>
        <p>{companyBlock}</p>

        <h2>Kontakt</h2>
        <p>Telefon: {settings.contact_phone}<br />
        E-Mail: <a href={`mailto:${settings.contact_email}`} className="text-[#A81C1C]">{settings.contact_email}</a></p>

        <h2>Umsatzsteuer-ID</h2>
        <p>Umsatzsteuer-Identifikationsnummer gemäß &sect; 27a Umsatzsteuergesetz:<br />
        DE318093819</p>

        <h2>Verantwortlich für den Inhalt nach &sect; 18 Abs. 2 MStV</h2>
        <p>{companyBlock}</p>

        <h2>EU-Streitschlichtung</h2>
        <p>Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:&nbsp;
        <a href="https://www.ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#A81C1C]">https://www.ec.europa.eu/consumers/odr/</a>.<br />
        Unsere E-Mail-Adresse finden Sie oben im Impressum.</p>

        <h2>Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
        <p>Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>

        <h2>Haftung für Inhalte</h2>
        <p>Als Diensteanbieter sind wir gemäß &sect; 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach &sect;&sect; 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.</p>

        <h2>Haftung für Links</h2>
        <p>Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.</p>

        <h2>Urheberrecht</h2>
        <p>Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.</p>
      </>
    ),
    'Datenschutz': (
      <>
        <h2>1. Datenschutz auf einen Blick</h2>
        <p>Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.</p>

        <h2>2. Verantwortliche Stelle</h2>
        <p>{companyBlock}<br />
        Telefon: {settings.contact_phone}<br />
        E-Mail: <a href={`mailto:${settings.contact_email}`} className="text-[#A81C1C]">{settings.contact_email}</a></p>
        <p>Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten entscheidet.</p>

        <h2>3. Datenerfassung auf dieser Website</h2>
        <h3>Cookies</h3>
        <p>Unsere Website verwendet Cookies. Das sind kleine Textdateien, die Ihr Webbrowser auf Ihrem Endgerät speichert. Cookies helfen uns dabei, unser Angebot nutzerfreundlicher und effektiver zu gestalten. Einige Cookies sind &bdquo;Session-Cookies&ldquo; und werden nach Ende Ihrer Browser-Sitzung automatisch gelöscht. Andere Cookies bleiben auf Ihrem Endgerät gespeichert, bis Sie diese löschen. Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden und Cookies nur im Einzelfall erlauben.</p>
        <h3>Server-Log-Dateien</h3>
        <p>Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind: Browsertyp und Browserversion, verwendetes Betriebssystem, Referrer URL, Hostname des zugreifenden Rechners, Uhrzeit der Serveranfrage, IP-Adresse. Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.</p>

        <h2>4. Bestellungen und Zahlungsabwicklung</h2>
        <p>Wenn Sie über unsere Website eine Bestellung aufgeben, erheben wir folgende Daten: Name, Adresse, Telefonnummer (für Lieferungen), Name (für Abholungen), E-Mail-Adresse (für Bestellbestätigungen), Zahlungsdaten (bei PayPal-Zahlungen über den PayPal-Dienst). Diese Daten werden ausschließlich zur Abwicklung Ihrer Bestellung verwendet und an unser Kassensystem (ExpertOrder) übermittelt. Die Rechtsgrundlage hierfür ist Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung). Ihre Bestelldaten werden für die Dauer der gesetzlichen Aufbewahrungsfristen (10 Jahre gemäß HGB/AO) gespeichert.</p>

        <h2>5. PayPal</h2>
        <p>Auf dieser Website bieten wir die Zahlung über PayPal an. Anbieter dieses Zahlungsdienstes ist die PayPal (Europe) S.&agrave;.r.l. et Cie, S.C.A., 22-24 Boulevard Royal, L-2449 Luxembourg. Wenn Sie mit PayPal bezahlen, erfolgt eine Übermittlung der von Ihnen eingegebenen Zahlungsdaten an PayPal. Die Übermittlung Ihrer Daten an PayPal erfolgt auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) und Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).</p>

        <h2>6. E-Mail-Versand (Resend)</h2>
        <p>Für den Versand von Bestellbestätigungen nutzen wir den Dienst Resend (Resend, Inc.). Ihre E-Mail-Adresse wird ausschließlich für den Versand transaktionaler E-Mails im Rahmen Ihrer Bestellung verwendet. Die Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO.</p>

        <h2>6.1 Newsletter / Marketing-E-Mails</h2>
        <p>Wenn Sie zugestimmt haben, Marketing-E-Mails von uns zu erhalten (z.B. Aktionen, neue Gerichte, Rabatt-Codes), nutzen wir den gleichen Dienst Resend für den Versand. Sie können sich jederzeit abmelden:</p>
        <ul>
          <li><strong>Direkt-Link</strong> am Ende jeder Marketing-E-Mail (&bdquo;Hier abmelden&ldquo;)</li>
          <li><strong>Manuelles Formular:</strong> <a href="/api/email/unsubscribe-manual" className="text-[#A81C1C] hover:underline" data-testid="datenschutz-unsubscribe-link">https://zozo-burger.de/api/email/unsubscribe-manual</a> &mdash; falls der Direkt-Link nicht funktioniert, einfach Ihre E-Mail eintragen und Sie werden sofort aus dem Verteiler genommen</li>
          <li><strong>Per E-Mail:</strong> <a href={`mailto:${settings.contact_email}?subject=Newsletter abmelden`} className="text-[#A81C1C] hover:underline">{settings.contact_email}</a> mit der Bitte um Abmeldung</li>
        </ul>
        <p>Nach Ihrer Abmeldung wird Ihre E-Mail-Adresse in unsere Sperrliste eingetragen und Sie erhalten keine Marketing-E-Mails mehr. Bestellbestätigungen und Service-Nachrichten zu offenen Bestellungen bleiben davon unberührt. Die Rechtsgrundlage für Marketing-E-Mails ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).</p>

        <h2>6a. Kundenwiedererkennung (localStorage)</h2>
        <p>Mit Ihrer ausdrücklichen Einwilligung (Checkbox im Bestellvorgang) speichern wir einen kryptographisch sicheren, nicht-erratbaren Erkennungs-Token in Ihrem Browser (localStorage). Dieser Token dient ausschließlich dazu, Sie bei einem erneuten Besuch wiederzuerkennen, damit wir Ihnen folgende Komfortfunktionen anbieten können:</p>
        <ul>
          <li>Automatische Vorbefüllung Ihrer Kontaktdaten und Lieferadresse im Bestellvorgang</li>
          <li>Anzeige einer personalisierten Begrüßungsnachricht mit Ihrer letzten Bestellung</li>
          <li>Einfache Nachbestellung mit einem Klick</li>
        </ul>
        <p><strong>Was wird gespeichert?</strong> Im localStorage Ihres Browsers wird ausschließlich ein technischer Token (zufällige Zeichenkette) und eine Kunden-ID gespeichert. Keine personenbezogenen Daten wie Name, Adresse oder E-Mail werden im Browser gespeichert – diese werden bei jedem Seitenaufruf vom Server abgerufen.</p>
        <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Sie können Ihre Einwilligung jederzeit widerrufen, indem Sie unter &bdquo;Meine Bestellungen&ldquo; auf &bdquo;Meine Daten löschen&ldquo; klicken. Dadurch werden Ihr Kundenprofil, Ihre gespeicherten Adressen und der localStorage-Token gelöscht. Bestelldaten werden anonymisiert.</p>
        <p><strong>Magic Link:</strong> Über die Funktion &bdquo;Meine Bestellungen&ldquo; können Sie einen zeitlich begrenzten Zugangslink (24 Stunden gültig) per E-Mail anfordern. Dieser Link ermöglicht den Zugang zu Ihrer Bestellhistorie und Adressverwaltung ohne Passwort. Der Link ist einmalig verwendbar und wird nach Nutzung ungültig.</p>

        <h2>6b. ZOZO Punkte Treueprogramm</h2>
        <p>Mit Ihrer ausdrücklichen Einwilligung (Checkbox &bdquo;Am ZOZO Punkte-Programm teilnehmen&ldquo;) nehmen Sie an unserem Treueprogramm teil. Dabei werden folgende Daten verarbeitet:</p>
        <ul>
          <li>Ihr Punktekontostand und Transaktionshistorie (verdiente, eingelöste und verfallene Punkte)</li>
          <li>Verknüpfung mit Ihrem Kundenprofil über E-Mail-Adresse oder Telefonnummer</li>
        </ul>
        <p><strong>Zweck:</strong> Verwaltung des Treueprogramms, Berechnung und Gutschrift von Punkten, Einlösung von Punkterabatten.</p>
        <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) und Art. 6 Abs. 1 lit. b DSGVO (Vertragsdurchführung).</p>
        <p><strong>Verfall:</strong> Punkte verfallen nach 18 Monaten ohne Bestellaktivität. Eine Erinnerung erfolgt nach 12 Monaten.</p>
        <p><strong>Widerruf:</strong> Sie können Ihr Punktekonto jederzeit unter &bdquo;Meine Bestellungen&ldquo; &rarr; &bdquo;Einstellungen&ldquo; löschen. Dabei werden alle Punkte und Transaktionen unwiderruflich gelöscht.</p>

        <h2>7. Ihre Rechte</h2>
        <p>Sie haben jederzeit das Recht:</p>
        <ul>
          <li>Auskunft über Ihre gespeicherten personenbezogenen Daten zu erhalten (Art. 15 DSGVO)</li>
          <li>Die Berichtigung unrichtiger Daten zu verlangen (Art. 16 DSGVO)</li>
          <li>Die Löschung Ihrer Daten zu verlangen (Art. 17 DSGVO)</li>
          <li>Die Einschränkung der Verarbeitung zu verlangen (Art. 18 DSGVO)</li>
          <li>Datenübertragbarkeit zu verlangen (Art. 20 DSGVO)</li>
          <li>Sich bei einer Aufsichtsbehörde zu beschweren (Art. 77 DSGVO)</li>
        </ul>
        <p>Zuständige Aufsichtsbehörde: Unabhängiges Landeszentrum für Datenschutz Schleswig-Holstein (ULD), Holstenstraße 98, 24103 Kiel.</p>

        <h2>8. SSL-Verschlüsselung</h2>
        <p>Diese Seite nutzt aus Sicherheitsgründen eine SSL-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von &bdquo;http://&ldquo; auf &bdquo;https://&ldquo; wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.</p>

        <h2>9. Google Maps / Places API</h2>
        <p>Für die komfortable Adresseingabe im Bestellvorgang nutzen wir die Google Places Autocomplete-Funktion. Dabei werden Ihre Eingaben im Adressfeld an die Server von Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA, übermittelt, um passende Adressvorschläge anzuzeigen.</p>
        <p>Die Nutzung erfolgt auf Grundlage unseres berechtigten Interesses (Art. 6 Abs. 1 lit. f DSGVO) an einer benutzerfreundlichen und fehlerfreien Adresseingabe. Es gelten die <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#A81C1C] hover:underline">Datenschutzbestimmungen von Google</a>.</p>

        <h2>10. Standortermittlung</h2>
        <p>Wenn Sie im Bestellprozess die Funktion &bdquo;Meinen Standort verwenden&ldquo; nutzen, werden Ihre GPS-Koordinaten einmalig zur Ermittlung Ihrer Lieferadresse verwendet. Die Koordinaten werden an die Google Geocoding API übermittelt, um eine Straßenadresse zu ermitteln. Ihre GPS-Koordinaten werden NICHT gespeichert und nicht für andere Zwecke verwendet. Die Nutzung dieser Funktion ist freiwillig &ndash; Sie können Ihre Adresse jederzeit auch manuell eingeben. Es gelten die <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#A81C1C] hover:underline">Datenschutzbestimmungen von Google</a>.</p>
        <p>Die Rechtsgrundlage für die Verarbeitung Ihrer Standortdaten ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), da Ihr Browser Sie aktiv um Erlaubnis für den Standortzugriff bittet.</p>

        <h2>11. Adressverifizierung</h2>
        <p>Zur Prüfung Ihrer Lieferadresse nutzen wir die Google Geocoding API. Ihre eingegebene Adresse wird einmalig an Google übermittelt, um die Existenz der Adresse zu bestätigen, die Entfernung zu unserem Restaurant zu berechnen und die voraussichtliche Lieferzeit zu ermitteln. Das Ergebnis wird für 24 Stunden in unserer Datenbank zwischengespeichert, um wiederholte Abfragen zu vermeiden. Die Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an korrekter Lieferung). Es gelten die <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#A81C1C] hover:underline">Datenschutzbestimmungen von Google</a>.</p>

        <h2>12. Zahlungsverarbeitung (PayPal)</h2>
        <p>Wenn Sie sich für die Zahlungsmethode PayPal entscheiden, werden Sie zur Abwicklung der Zahlung auf die Website von PayPal (Europe) S.&agrave; r.l. et Cie, S.C.A., 22-24 Boulevard Royal, L-2449 Luxemburg, weitergeleitet. Ihre Zahlungsdaten (z.B. PayPal-Kontodaten, Bankverbindung) werden ausschließlich von PayPal verarbeitet. Wir erhalten von PayPal lediglich eine Bestätigung über die erfolgreiche Zahlung, die PayPal-Transaktions-ID und den Zahlungsbetrag.</p>
        <p>Wir speichern die PayPal-Transaktions-ID, den Zahlungsbetrag und den Zahlungsstatus zur Bestellabwicklung und Buchführung. Ihre PayPal-Kontodaten (E-Mail, Bankverbindung) werden bei uns NICHT gespeichert. Zur Fehlerbehebung protokollieren wir den Erfolg oder Misserfolg von Zahlungsvorgängen (ohne personenbezogene PayPal-Daten). Die Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung). Es gelten die <a href="https://www.paypal.com/de/legalhub/privacy-full" target="_blank" rel="noopener noreferrer" className="text-[#A81C1C] hover:underline">Datenschutzbestimmungen von PayPal</a>.</p>

        <h2>13. Social-Media-Integration (Instagram/Meta)</h2>
        <p>Unser Restaurant nutzt die Meta/Instagram Graph API zur automatisierten Veröffentlichung von Beiträgen auf unserem Instagram-Business-Account. Der Zugangs-Token wird verschlüsselt in unserer Datenbank gespeichert und ausschließlich zur Veröffentlichung von Beiträgen verwendet. Es werden keine personenbezogenen Daten unserer Kunden an Meta/Instagram übermittelt. Die Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Öffentlichkeitsarbeit). Es gelten die <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-[#A81C1C] hover:underline">Datenschutzbestimmungen von Meta</a>.</p>
      </>
    ),
    'AGB': (
      <>
        <p className="text-neutral-400 text-sm mb-8"><strong>Allgemeine Geschäftsbedingungen (AGB)</strong><br />{settings.shop_name} &ndash; {settings.company_owner}, {settings.company_street}, {settings.company_postal_code} {settings.company_city}</p>

        <h2>&sect; 1 Geltungsbereich</h2>
        <p>Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Bestellungen, die über die Website zozo-burger.de, den Kiosk-Bestellterminal oder telefonisch bei ZOZO Burger aufgegeben werden. Mit der Aufgabe einer Bestellung akzeptiert der Kunde diese AGB.</p>

        <h2>&sect; 2 Vertragsschluss</h2>
        <p>(1) Die Darstellung der Produkte auf unserer Website stellt kein rechtlich bindendes Angebot, sondern eine Aufforderung zur Bestellung dar.</p>
        <p>(2) Der Vertrag kommt zustande, wenn wir Ihre Bestellung durch eine Bestellbestätigung (per E-Mail oder auf der Bestätigungsseite) annehmen.</p>
        <p>(3) Wir behalten uns vor, Bestellungen ohne Angabe von Gründen abzulehnen, insbesondere wenn Produkte nicht verfügbar sind oder die angegebene Lieferadresse außerhalb unseres Liefergebiets liegt.</p>
        <p>(4) Der Vertragstext wird von uns gespeichert. Sie können den Vertragstext vor Absenden der Bestellung über die Druckfunktion Ihres Browsers ausdrucken. Die Bestellbestätigung mit allen Bestelldaten wird Ihnen per E-Mail zugesandt.</p>

        <h2>&sect; 3 Preise und Zahlung</h2>
        <p>(1) Alle angegebenen Preise sind Endpreise inklusive der gesetzlichen Mehrwertsteuer.</p>
        <p>(2) Zusätzlich zu den Produktpreisen können Liefergebühren anfallen. Diese werden im Bestellvorgang vor Abschluss der Bestellung transparent angezeigt.</p>
        <p>(3) Die Zahlung erfolgt wahlweise per Barzahlung bei Lieferung bzw. Abholung oder per PayPal.</p>
        <p>(4) Bei Abholbestellungen gewähren wir einen Rabatt von 10% auf den Warenwert. Dieser wird automatisch im Checkout berechnet.</p>
        <p>(5) Rabattcodes können im Checkout-Vorgang eingelöst werden. Pro Bestellung kann nur ein Rabattcode verwendet werden. Rabattcodes sind nicht mit anderen Rabatten kombinierbar, sofern nicht ausdrücklich anders angegeben.</p>

        <h2>&sect; 4 Gutscheine und Rabattaktionen</h2>
        <p>(1) Von ZOZO Burger ausgegebene Gutscheine und Rabattcodes sind nur im Rahmen der jeweiligen Aktionsbedingungen einlösbar.</p>
        <p>(2) Gutscheine sind nicht übertragbar, nicht mit anderen Aktionen kombinierbar und können nicht bar ausgezahlt werden, sofern nicht anders angegeben.</p>
        <p>(3) Pro Bestellung kann nur ein Gutschein- oder Rabattcode eingesetzt werden. Einige Gutscheine können an einen Mindestbestellwert gekoppelt sein.</p>
        <p>(4) ZOZO Burger behält sich das Recht vor, Gutscheine bei missbr&auml;uchlicher Nutzung (z.&nbsp;B. Mehrfachnutzung, automatisierte Einlösung) zu sperren.</p>

        <h2>&sect; 5 Lieferung und Abholung</h2>
        <p>(1) Wir liefern innerhalb unseres auf der Website angegebenen Liefergebiets. Die Zugehörigkeit zum Liefergebiet kann durch Eingabe der Postleitzahl auf der Website geprüft werden.</p>
        <p>(2) Die angegebenen Lieferzeiten sind ungefähre Angaben und können je nach Bestellaufkommen variieren. Eine Garantie für die Einhaltung der angegebenen Lieferzeit wird nicht übernommen.</p>
        <p>(3) Bei Abholbestellungen wird die Bestellung am gewählten Standort zur Abholung bereitgestellt. Der Kunde wird über die Fertigstellung informiert.</p>
        <p>(4) Wunschzeit-Bestellungen sind möglich und können im Checkout ausgewählt werden. Die früheste Wunschzeit beträgt 30 Minuten ab Bestellzeitpunkt.</p>
        <p>(5) Ist der Kunde bei Lieferung nicht unter der angegebenen Adresse anzutreffen, wird der Lieferfahrer versuchen, den Kunden telefonisch zu erreichen. Ist der Kunde nach angemessener Wartezeit (ca. 5 Minuten) weiterhin nicht erreichbar, behält sich ZOZO Burger das Recht vor, die Bestellung als zugestellt zu behandeln. Ein erneuter Lieferversuch oder eine Rückerstattung ist in diesem Fall ausgeschlossen.</p>
        <p>(6) Die Gefahr des zufälligen Untergangs oder der zufälligen Verschlechterung der Ware geht bei Lieferbestellungen mit Übergabe an den Kunden oder eine empfangsberechtigte Person über. Bei Abholung geht die Gefahr mit Aushändigung der Ware am Standort über.</p>
        <p>(7) Es kann ein Mindestbestellwert für Lieferungen gelten. Dieser wird auf der Website und im Bestellprozess transparent angezeigt.</p>

        <h2>&sect; 6 Kiosk-Bestellterminal</h2>
        <p>(1) Bestellungen über den Kiosk-Bestellterminal in unseren Filialen unterliegen denselben AGB wie Online-Bestellungen, sofern nicht nachfolgend abweichend geregelt.</p>
        <p>(2) Der Vertrag bei Kiosk-Bestellungen kommt mit der Bestätigung der Bestellung am Terminal und der Zahlung zustande.</p>
        <p>(3) Die Zahlung am Kiosk erfolgt per Kartenzahlung oder Barzahlung an der Theke. Die verfügbaren Zahlungsarten werden am Terminal angezeigt.</p>
        <p>(4) Kiosk-Bestellungen sind grundsätzlich Abholbestellungen. Die Bestellung wird zur Abholung an der Theke bereitgestellt.</p>

        <h2>&sect; 7 Stornierung und Widerrufsrecht</h2>
        <p>(1) Eine Stornierung der Bestellung ist nur möglich, solange mit der Zubereitung noch nicht begonnen wurde. Bitte kontaktieren Sie uns hierfür telefonisch unter 04101 39 84 850.</p>
        <p>(2) Bei bereits zubereiteten oder gelieferten Bestellungen ist eine Stornierung ausgeschlossen.</p>
        <p>(3) <strong>Widerrufsbelehrung:</strong> Das gesetzliche Widerrufsrecht besteht nicht bei Verträgen zur Lieferung von Waren, die schnell verderben können oder deren Verfallsdatum schnell überschritten würde (&sect; 312g Abs. 2 Nr. 2 BGB). Da es sich bei unseren Produkten um frisch zubereitete Lebensmittel handelt, ist ein Widerrufsrecht nach Beginn der Zubereitung ausgeschlossen.</p>
        <p>(4) Bei Bestellungen, deren Zubereitung noch nicht begonnen hat, können Sie die Bestellung innerhalb von 5 Minuten nach Bestellaufgabe kostenlos stornieren.</p>

        <h2>&sect; 8 Mängel und Reklamation</h2>
        <p>(1) Sollte Ihre Bestellung mangelhaft sein (z.&nbsp;B. falsche Produkte, Qualitätsmängel), kontaktieren Sie uns bitte umgehend unter 04101 39 84 850 oder info@zozo-burger.de.</p>
        <p>(2) Wir werden uns um eine schnelle und faire Lösung bemühen (Ersatzlieferung, Gutschrift oder Rückerstattung).</p>
        <p>(3) Bitte bewahren Sie mangelhaft gelieferte Ware nach Möglichkeit auf und dokumentieren Sie den Mangel (z.&nbsp;B. durch Fotos), damit wir den Mangel nachvollziehen können.</p>
        <p>(4) Reklamationen müssen zeitnah, spätestens jedoch innerhalb von 24 Stunden nach Erhalt der Bestellung, erfolgen.</p>

        <h2>&sect; 9 Allergene, Zusatzstoffe und Lebensmittelsicherheit</h2>
        <p>(1) Informationen zu Allergenen und Zusatzstoffen finden Sie in der Produktbeschreibung auf unserer Website oder erfragen Sie telefonisch. Die Kennzeichnung erfolgt gemäß der EU-Lebensmittelinformationsverordnung (LMIV, VO (EU) Nr. 1169/2011).</p>
        <p>(2) Wir kennzeichnen die 14 Hauptallergene (Glutenhaltiges Getreide, Krebstiere, Eier, Fisch, Erdnüsse, Soja, Milch, Schalenfrüchte, Sellerie, Senf, Sesam, Schwefeldioxid/Sulfite, Lupinen, Weichtiere) in unseren Produktbeschreibungen.</p>
        <p>(3) Trotz sorgfältiger Zubereitung können Kreuzkontaminationen nicht vollständig ausgeschlossen werden. Personen mit schweren Allergien werden gebeten, uns vor der Bestellung zu kontaktieren.</p>
        <p>(4) Unsere Produkte werden unter Einhaltung der geltenden Hygienevorschriften (HACCP) zubereitet und verpackt.</p>
        <p>(5) Bei Lieferungen empfehlen wir, die Speisen zeitnah nach Erhalt zu verzehren. ZOZO Burger übernimmt keine Haftung für Qualitätsverluste, die durch verspätete Entgegennahme oder unsachgemäße Lagerung durch den Kunden entstehen.</p>

        <h2>&sect; 10 Haftung</h2>
        <p>(1) Wir haften unbeschränkt für Vorsatz und grobe Fahrlässigkeit.</p>
        <p>(2) Für leichte Fahrlässigkeit haften wir nur bei Verletzung wesentlicher Vertragspflichten (Kardinalpflichten), begrenzt auf den vertragstypisch vorhersehbaren Schaden.</p>
        <p>(3) Die Haftung für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit bleibt unberührt.</p>
        <p>(4) Die vorstehenden Haftungsbeschränkungen gelten nicht für Ansprüche nach dem Produkthaftungsgesetz oder bei Übernahme einer Garantie.</p>

        <h2>&sect; 11 Höhere Gewalt (Force Majeure)</h2>
        <p>(1) ZOZO Burger haftet nicht für Verzögerungen oder Nichterfüllung von Verpflichtungen, die auf Umstände höherer Gewalt zurückzuführen sind. Hierzu zählen insbesondere: Naturkatastrophen, Pandemien, Epidemien, behördliche Anordnungen, Streiks, Stromausfälle, schwerwiegende Störungen der IT-Infrastruktur oder Lieferkettenunterbrechungen.</p>
        <p>(2) Im Falle höherer Gewalt wird ZOZO Burger den Kunden unverzüglich informieren und sich um eine angemessene Lösung bemühen (z.&nbsp;B. Stornierung, Gutschrift oder Verschiebung der Lieferung).</p>

        <h2>&sect; 12 Verfügbarkeit des Online-Bestellsystems</h2>
        <p>(1) ZOZO Burger bemüht sich um eine möglichst unterbrechungsfreie Verfügbarkeit der Website und des Bestellsystems. Eine Garantie für die ständige Verfügbarkeit wird jedoch nicht übernommen.</p>
        <p>(2) ZOZO Burger haftet nicht für vorübergehende Nichtverfügbarkeit der Website aufgrund von Wartungsarbeiten, technischen Störungen oder sonstigen Umständen, die außerhalb unserer Kontrolle liegen.</p>
        <p>(3) Bei Störungen des Bestellsystems können Bestellungen telefonisch aufgegeben werden.</p>

        <h2>&sect; 13 Geistiges Eigentum und Urheberrecht</h2>
        <p>(1) Alle Inhalte der Website zozo-burger.de (Texte, Bilder, Grafiken, Logos, Produktfotos, Designs) sind urheberrechtlich geschützt und Eigentum von ZOZO Burger oder lizenziert.</p>
        <p>(2) Jede Vervielfältigung, Bearbeitung, Verbreitung oder öffentliche Wiedergabe der Inhalte bedarf der vorherigen schriftlichen Zustimmung von ZOZO Burger.</p>
        <p>(3) Die Marke &bdquo;ZOZO Burger&ldquo; sowie alle zugehörigen Logos und Kennzeichen sind geschützt. Eine Nutzung ohne ausdrückliche Genehmigung ist untersagt.</p>

        <h2>&sect; 14 Datenschutz</h2>
        <p>Informationen zum Umgang mit Ihren personenbezogenen Daten finden Sie in unserer <a href="/datenschutz" className="text-[#A81C1C]">Datenschutzerklärung</a>.</p>

        <h2>&sect; 15 Online-Streitbeilegung und Verbraucherschlichtung</h2>
        <p>(1) Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: <a href="https://www.ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#A81C1C]">https://www.ec.europa.eu/consumers/odr/</a>.</p>
        <p>(2) Unsere E-Mail-Adresse finden Sie in unserem <a href="/impressum" className="text-[#A81C1C]">Impressum</a>.</p>
        <p>(3) Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>

        <h2>&sect; 16 Schlussbestimmungen</h2>
        <p>(1) Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts (CISG).</p>
        <p>(2) Ist der Kunde Verbraucher, gelten zusätzlich die zwingenden Verbraucherschutzbestimmungen des Staates, in dem der Kunde seinen gewöhnlichen Aufenthalt hat, sofern diese günstiger sind.</p>
        <p>(3) Ist der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder öffentlich-rechtliches Sondervermögen, ist Gerichtsstand für alle Streitigkeiten Pinneberg. Im Übrigen gelten die gesetzlichen Gerichtsstände.</p>
        <p>(4) Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein oder werden, so wird hierdurch die Wirksamkeit der übrigen Bestimmungen nicht berührt. Anstelle der unwirksamen Bestimmung gilt diejenige wirksame Bestimmung als vereinbart, die dem wirtschaftlichen Zweck der unwirksamen Bestimmung am nächsten kommt.</p>
        <p>(5) Änderungen und Ergänzungen dieser AGB bedürfen der Schriftform. Dies gilt auch für die Änderung dieser Schriftformklausel.</p>
        <p>(6) Es gilt jeweils die zum Zeitpunkt der Bestellung aktuelle Fassung dieser AGB.</p>

        <p className="text-neutral-500 text-sm mt-8">Stand: 23.03.2026</p>
      </>
    ),
    'Kontakt': (
      <>
        <h2>So erreichst du uns</h2>
        <div className="grid md:grid-cols-2 gap-6 not-prose mt-6">
          {(locations || []).map(loc => {
            const addr = loc.address || {};
            return (
              <div key={loc.slug} className="bg-neutral-800/50 border border-neutral-700 rounded-xl p-6">
                <h3 className="text-white font-semibold text-lg mb-3">{loc.name}</h3>
                <p className="text-neutral-400 text-sm">{addr.street}<br />{addr.postal_code} {addr.city}</p>
                {loc.phone && <p className="text-neutral-400 text-sm mt-2">Telefon: <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className="text-[#A81C1C]">{loc.phone}</a></p>}
                <p className="text-neutral-400 text-sm">Öffnungszeiten: {formatHours(loc)}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-6">E-Mail: <a href={`mailto:${settings.contact_email}`} className="text-[#A81C1C]">{settings.contact_email}</a></p>
      </>
    ),
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid={`page-${title.toLowerCase()}`}>
      <SEO title={title} description={DESC_MAP[title]} path={PATH_MAP[title] || '/'} />
      <div className="container mx-auto px-4">
        <h1 className="font-display text-4xl font-bold mb-8 text-white">{title}</h1>
        <div className="prose prose-neutral prose-invert max-w-3xl prose-headings:text-white prose-a:text-[#A81C1C] prose-strong:text-white">
          {content[title] || <p>Diese Seite wird noch erstellt.</p>}
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <SiteSettingsProvider>
          <LocationProvider>
            <CartProvider>
              <CustomerProvider>
                <AuthProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </AuthProvider>
              </CustomerProvider>
            </CartProvider>
          </LocationProvider>
        </SiteSettingsProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
